import Lenis from 'lenis'

/**
 * Momentum smooth scrolling — on a mouse wheel, and nowhere else.
 *
 * WHY IT IS GATED SO HARD
 *
 * Lenis replaces native scrolling with a rAF loop. On a desktop wheel that is a
 * genuine improvement: the page decelerates instead of stepping. On a touch
 * screen it is a liability — iOS momentum, rubber-band and the browser chrome
 * hide/show are all native behaviours that a JS scroller has to reimplement, and
 * never quite does. `syncTouch` is therefore left OFF (Lenis's default), so
 * phones and tablets scroll exactly as they did before this file existed.
 *
 * It is also disabled outright when:
 *   • the user asked for reduced motion — a hijacked scroll is the single most
 *     nauseating thing on this list, and the request is explicit;
 *   • the primary input is coarse (touch), checked with `(pointer: coarse)`
 *     rather than by sniffing the user agent.
 *
 * Both are live queries: plugging in a mouse, or changing the OS motion
 * setting, re-evaluates without a reload.
 *
 * Scroll-driven CSS (`animation-timeline: view()` in globals.css) keeps working,
 * because Lenis performs a real scroll rather than transforming a container.
 */

let instance: Lenis | null = null
let frame = 0

/**
 * ⚠️ TRUE WHILE AN OVERLAY OWNS THE SCROLL. SEE `setSmoothScrollPaused`.
 *
 * Module-level rather than a local, because `start()` can run while something is
 * already paused — plugging in a mouse, or the reduced-motion query flipping
 * back, re-enters `start()` from `initSmoothScroll`'s `sync`. A fresh Lenis
 * created at that moment would begin eating wheel events again underneath an
 * open menu, which is the exact bug this flag exists to prevent.
 */
let paused = false

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'
const COARSE_POINTER = '(pointer: coarse)'

const shouldEnable = (): boolean =>
  !window.matchMedia(REDUCED_MOTION).matches && !window.matchMedia(COARSE_POINTER).matches

const start = (): void => {
  if (instance) return

  instance = new Lenis({
    /*
     * LERP, NOT DURATION — and this is the whole difference between "smooth"
     * and "laggy".
     *
     * `duration` + `easing` starts a fixed-length animation for every wheel
     * notch, so turning the wheel quickly queues animations and the page keeps
     * gliding long after your hand stopped. Nothing is slow while that happens —
     * the scroll has simply stopped answering the wheel, and that disconnect is
     * what reads as lag.
     *
     * `lerp` is exponential damping instead: every frame closes a fixed fraction
     * of the remaining distance, so new input is absorbed immediately rather
     * than fighting an animation already in flight.
     *
     * Measured on the built home page, 1440x900, 25 wheel notches 30 ms apart,
     * reporting how long the page kept moving perceptibly (>= 2 px/frame) after
     * the last notch. All four ran at a clean 60 fps — median frame 16.7 ms —
     * so this was never a frame-rate problem:
     *
     *   native scroll (reference)   -189 ms   <- the measurement's own offset
     *   duration 1.05 + easing       238 ms   ~420 ms of glide. The old setting.
     *   lerp 0.14                    120 ms   ~310 ms
     *   lerp 0.20                    -10 ms   ~180 ms   <- chosen
     *   lerp 0.28                    -43 ms   ~146 ms
     *
     * 0.20 stops with the wheel while keeping the deceleration that makes a
     * wheel feel better than native stepping. Lower is floatier; much above
     * 0.28 there is no point running Lenis at all.
     *
     * Re-measure with scripts/, not by feel — "smooth" and "responsive" pull in
     * opposite directions and the eye is a poor judge of which one is missing.
     */
    lerp: 0.2,
    // OFF on purpose. See the note above — native touch scrolling wins.
    syncTouch: false,
    smoothWheel: true,
  })

  const raf = (time: number): void => {
    instance?.raf(time)
    frame = window.requestAnimationFrame(raf)
  }

  frame = window.requestAnimationFrame(raf)

  // A Lenis created while an overlay is open must not start listening.
  if (paused) instance.stop()
}

const stop = (): void => {
  if (frame) window.cancelAnimationFrame(frame)
  frame = 0
  instance?.destroy()
  instance = null
}

/**
 * Starts smooth scrolling if this device and this user should have it, and
 * keeps that decision current. Returns a cleanup that fully tears it down.
 */
export const initSmoothScroll = (): (() => void) => {
  const queries = [window.matchMedia(REDUCED_MOTION), window.matchMedia(COARSE_POINTER)]

  const sync = (): void => {
    if (shouldEnable()) start()
    else stop()
  }

  sync()
  for (const query of queries) query.addEventListener('change', sync)

  return () => {
    for (const query of queries) query.removeEventListener('change', sync)
    stop()
  }
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * HAND THE SCROLL BACK TO THE BROWSER WHILE AN OVERLAY IS OPEN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ THIS IS THE FIX FOR THE CLIENT'S § 3.1: *"When the side menu is open,
 * normal scrolling becomes locked. The user should be able to scroll naturally
 * through the menu instead of having to manually navigate down just to reach
 * options such as Login."*
 *
 * The cause was not the body scroll lock — that part is correct and deliberate,
 * because the page behind a modal sheet must not move. It was LENIS.
 *
 * `smoothWheel` installs a `wheel` listener on `window` with
 * `{ passive: false }` and calls `preventDefault()` on every notch, then moves
 * the DOCUMENT's scroll position in its own rAF loop. So inside a
 * `position: fixed` overlay with its own `overflow-y-auto`:
 *
 *   • the wheel event is cancelled, so the overlay never scrolls;
 *   • Lenis scrolls the document instead, which is `overflow: hidden`, so
 *     nothing moves there either.
 *
 * The net effect is a menu that cannot be scrolled by any pointer at all — and
 * the only way down is Tab, which is literally what the client described as
 * "having to manually navigate down".
 *
 * ⚠️ AND IT WAS INVISIBLE ON A PHONE, WHICH IS WHY IT SURVIVED REVIEW.
 * Lenis is gated off `(pointer: coarse)` (see the header), so a phone and a
 * tablet scrolled the sheet natively and correctly the whole time. The menu is
 * `xl:hidden`, so the broken window is **a laptop or desktop between 0 and
 * 1279 px with a mouse or trackpad** — which is exactly where the client was.
 * Any future fix here has to be checked at BOTH ends: a phone, where Lenis is
 * absent, and a 1024 px window with a mouse, where it is not.
 *
 * `stop()` detaches nothing, it just makes Lenis ignore input, so the scroll
 * position it is holding survives and `start()` resumes without a jump. The
 * sheet also carries `data-lenis-prevent`, which is Lenis's own opt-out for a
 * scrollable descendant; both are kept because they fail in opposite
 * directions — the attribute is lost if the markup is restructured, and this
 * call is lost if an overlay is added without it.
 */
export const setSmoothScrollPaused = (next: boolean): void => {
  paused = next
  if (!instance) return
  if (next) instance.stop()
  else instance.start()
}

/**
 * Jump to the top of the document.
 *
 * Routed through here rather than calling `window.scrollTo` directly: while
 * Lenis owns the scroll position, a raw `scrollTo` fights its rAF loop and the
 * page visibly snaps back.
 */
export const scrollToTop = (immediate: boolean): void => {
  if (instance) {
    instance.scrollTo(0, { immediate })
    return
  }

  window.scrollTo({ top: 0, left: 0, behavior: immediate ? 'auto' : 'smooth' })
}
